import React, { useEffect, useState } from "react";
import { MapPin, Phone, Search, User, X, ChevronDown, ChevronUp } from "lucide-react";

import styles from "./RightSidebar.module.css";
import EmptyProfileImage from '../../../../../images/navbar/empty-profile-image.png';

// API Services
import { getActiveHomeUserData } from "../../../Services/getActiveHomeUserData";
import { getBusinessProfileData } from "../../../../HomeMessage/Services/getBusinessProfileData";
import { getSearchMessages } from "../../../Services/getSearchMessages";

// Child Components
import StarRating from "../../../../StarRating/startRating";
import HomeUserProfilePureCSS from '../../../../../profiles/HomeProfilePureCSS';
import BusinessProfilePureCSS from '../../../../../profiles/BusinessProfilePureCSS';

// Helper function to build the address safely
const formatAddress = (model) => {
    const addressParts = [
        model.streetNumber, model.streetName, model.suburb, model.myState, model.postCode
    ];
    return addressParts.filter(part => part).join(', ');
};

// Helper to format review count text
const formatReviewText = (count) => {
    if (!count && count !== 0) return '';
    return `(${count} review${count !== 1 ? 's' : ''})`;
};

function RightSidebar({ conversationSearchId, setChatSearching, activeConversation, homeData }) {
  // --- CONSOLIDATED STATE ---
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // --- MODAL & UI STATE ---
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isBioExpanded, setIsBioExpanded] = useState(false); // <-- NEW STATE

  // --- SEARCH STATE ---
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);

  useEffect(() => {
    if (!activeConversation || !homeData?.userId) {
      setProfileData(null);
      return;
    }

    const fetchProfileData = async () => {
      setLoading(true);
      setError(null);
      setProfileData(null);
      // Reset UI states
      setIsSearchActive(false);
      setIsBioExpanded(false);
      setSearchText("");
      setFilteredMessages([]);

      try {
        if (homeData.userType === 'Home') {
          const response = await getActiveHomeUserData(homeData.userId);
          const { user } = response;
          console.log("User object:", user);

          // --- NEW: DEVELOPER LOGIC ---
          const devName = user.developerBusinessName;
          console.log("DEV:", devName);
          const isDeveloper = !!devName; // Convert to boolean: true if devName exists, false otherwise


          let userTagline = "Homeowner";
          let userLocation = user.postcode;
          let userPhone = null; // Regular homeowners do not show a phone number

          if (isDeveloper) {
              userTagline = "Developer";
              // **IMPORTANT**: For this to work, your `getActiveHomeUserData` API response
              // must include the user's address and phone fields.
              userLocation = formatAddress(user); // Assumes 'user' object has address fields
              userPhone = user.phone;
          }


          setProfileData({
            profileType: 'Home',
            id: homeData.userId,
            name: `${user.firstName} ${user.lastName} (${devName})`,
            profilePic: user.image,
            tagline: userTagline, // Use the new dynamic tagline
            location: userLocation, // Use the new dynamic location
            phone: userPhone,
          });

        } else if (homeData.userType === 'Business') {
          // This API takes the Business's UserID
          const response = await getBusinessProfileData(homeData.userId);
          const { model } = response;
          
          setProfileData({
            profileType: 'Business',
            id: model.businessId,
            name: model.businessName,
            profilePic: model.profilePic,
            tagline: model.businessDescription || model.overview, // Use new field with fallback
            bio: model.businessBio, // <-- NEW
            rating: model.overallRating,
            reviewCount: model.reviewCount, // <-- NEW
            googleRating: model.googleRating, // <-- NEW
            googleRatingCount: model.googleRatingCount, // <-- NEW
            location: formatAddress(model),
            phone: model.phone,
          });
        }
      } catch (err) {
        console.error("Failed to fetch profile data:", err);
        setError("Could not load profile details.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [activeConversation, homeData?.userId, homeData?.userType]);

  const handleToggleSearch = () => {
    setIsSearchActive(!isSearchActive);
    setSearchText("");
    setFilteredMessages([]);
    setSearchAttempted(false);
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    if (!searchText.trim()) return;

    setSearchLoading(true);
    setSearchAttempted(true);
    try {
      const results = await getSearchMessages(conversationSearchId, searchText);
      setFilteredMessages(results?.searchResponse || []);
    } catch (error) {
      console.error("Failed to fetch search results:", error);
      setFilteredMessages([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // --- NEW: Bio Truncation Logic ---
  const BIO_MAX_LENGTH = 180;
  const isBioLong = profileData?.bio && profileData.bio.length > BIO_MAX_LENGTH;
  
  const getBioText = () => {
      if (!profileData?.bio) return '';
      if (isBioLong && !isBioExpanded) {
          return `${profileData.bio.substring(0, BIO_MAX_LENGTH)}...`;
      }
      return profileData.bio;
  };

  if (!activeConversation) {
    return <div className={styles.sidebarContainer} />;
  }

  return (
    <>
      <div className={styles.sidebarContainer}>
        <div className={styles.profileCard}>
          {loading && <p className={styles.infoText}>Loading profile...</p>}
          {error && <p className={styles.errorText}>{error}</p>}

          {profileData && (
            <>
              {/* --- HEADER (No Changes) --- */}
              <div className={styles.profileHeader}>
                 <div className={styles.headerActions}>
                  <button onClick={handleToggleSearch} className={styles.headerActionButton} title="Search Messages">
                    {isSearchActive ? <X size={20} /> : <Search size={20} />}
                  </button>
                  <button onClick={() => setIsProfileModalOpen(true)} className={styles.headerActionButton} title="View Profile">
                    <User size={20} />
                  </button>
                </div>
                <img src={profileData.profilePic || EmptyProfileImage} alt={profileData.name} className={styles.profileImage} />
                <h3 className={styles.businessName} title={profileData.name}>{profileData.name}</h3>
                {profileData.tagline && !isSearchActive && (<p className={styles.description}>{profileData.tagline}</p>)}
              </div>

              {/* --- SEARCH PANEL (No Changes) --- */}
              <div className={`${styles.searchPanel} ${isSearchActive ? styles.searchPanelActive : ''}`}>
                {/* Search JSX is unchanged */}
                 <div className={styles.searchHeader}><h4 className={styles.sectionTitle}>Search Conversation</h4></div>
                <form onSubmit={handleSearchSubmit}>
                  <div className={styles.searchInputContainer}>
                    <Search size={18} className={styles.searchInputIcon} />
                    <input type="text" placeholder="Search messages..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className={styles.searchInput} />
                  </div>
                </form>
                <div className={styles.searchResultsList}>
                    {searchLoading && <p className={styles.infoText}>Searching...</p>}
                    {!searchLoading && searchAttempted && filteredMessages.length === 0 && (<p className={styles.infoText}>No messages found.</p>)}
                    {filteredMessages.map((msg) => (<div key={msg.messageId} className={styles.searchResultItem} onClick={() => { setChatSearching(msg); handleToggleSearch(); }}><div className={styles.resultHeader}><span className={styles.resultUser}>{msg.businessUserName || msg.homeUserName}</span><span className={styles.resultDate}>{new Date(msg.messageTime).toLocaleDateString()}</span></div><p className={styles.resultMessage}>{msg.message}</p></div>))}
                </div>
              </div>
              
              {/* --- UPDATED: Profile Content --- */}
              <div className={`${styles.profileContent} ${isSearchActive ? styles.profileContentHidden : ''}`}>
                {profileData.profileType === 'Business' && (
                  <>
                    <div className={styles.ratingsSection}>
                      <div className={styles.ratingItem}>
                        <StarRating ratings={profileData.rating} />
                        <span className={styles.ratingText}>
                          {profileData.rating?.toFixed(1)} {formatReviewText(profileData.reviewCount)}
                        </span>
                      </div>
                      {profileData.googleRating && profileData.googleRatingCount > 0 && (
                        <div className={styles.ratingItem}>
                          <span className={styles.googleRatingText}>
                            Google Rating: {profileData.googleRating.toFixed(1)} {formatReviewText(profileData.googleRatingCount)}
                          </span>
                        </div>
                      )}
                    </div>

                    {profileData.bio && (
                      <div className={styles.bioSection}>
                        <h4 className={styles.sectionTitle}>About Us</h4>
                        <p className={styles.bioText}>{getBioText()}</p>
                        {isBioLong && (
                          <button className={styles.readMoreButton} onClick={() => setIsBioExpanded(!isBioExpanded)}>
                            {isBioExpanded ? 'Show Less' : 'Read More'}
                            {isBioExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}

                <div className={styles.contactSection}>
                  <h4 className={styles.sectionTitle}>Information</h4>
                  <div className={styles.contactList}>
                    {profileData.location && (<div className={styles.contactItem}><MapPin size={18} className={styles.contactIcon} /><span>{profileData.location}</span></div>)}
                    {profileData.phone && (<div className={styles.contactItem}><Phone size={18} className={styles.contactIcon} /><span>{profileData.phone}</span></div>)}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* --- MODALS (No Changes) --- */}
      {profileData?.profileType === 'Home' && (<HomeUserProfilePureCSS showMessageButton={false} isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} userId={profileData.id} />)}
      {profileData?.profileType === 'Business' && (<BusinessProfilePureCSS showMessageButton={false} isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} businessId={profileData.id} useIDTYPE={"BID"} />)}
    </>
  );
}

export default RightSidebar;